import { AlertIcon, Button, CalendarIcon, Dialog, SuccessIcon } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { InvitationRecipientType } from '../../types';

type NewEnrollmentInvitationConfirmationModalProps = {
  tuitionAndFees: {
    tuition: {
      dueDate: Date;
      proratedDueDate: Date;
      proratedAmount: number;
    };
  };
  documents: { name: string; id: string; url?: string | null | undefined }[];
  displayName: string;
  enrollmentDate: string;
  automaticallyEnroll: boolean;
  enrollmentStatus: string;
  invitationRecipients: InvitationRecipientType[];
  isOpen: boolean;
  onClose: () => void;
  /**
   * The share that grants the family access to `documents` can fail (CCMS-8210), and
   * this modal used to announce "documents sent" purely because documents were
   * selected. Without this the modal states the opposite of what happened.
   */
  documentSharingFailed?: boolean;
};

const NewEnrollmentInvitationConfirmationModal = ({
  tuitionAndFees,
  documents,
  displayName,
  enrollmentDate,
  automaticallyEnroll,
  enrollmentStatus,
  invitationRecipients = [],
  isOpen,
  onClose,
  documentSharingFailed = false,
}: NewEnrollmentInvitationConfirmationModalProps) => {
  const { t } = useTranslation();

  const nextInvoiceDate =
    tuitionAndFees && Object.keys(tuitionAndFees).length > 0 && tuitionAndFees.tuition
      ? tuitionAndFees.tuition.dueDate
      : null;

  const getInvitationRecipientsNames = () => {
    if (invitationRecipients.length > 1) {
      const displayNames = invitationRecipients.map((contact: InvitationRecipientType) => contact.displayName);
      return displayNames.join(',');
    }

    if (invitationRecipients.length > 0) {
      return invitationRecipients[0].displayName;
    }

    return '';
  };

  const getEnrollmentText = (): string => {
    const studentInfo = {
      name: displayName,
      date: dayjs.utc(enrollmentDate).format('MM/DD'),
    };
    if (dayjs.utc(enrollmentDate).isBefore(dayjs())) {
      return t('enrollments.childPastEnrolled', studentInfo);
    }
    if (automaticallyEnroll) {
      return t('enrollments.childAutomaticallyEnrolled', studentInfo);
    }
    return t('enrollments.childEnrolled', studentInfo);
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} hasCloseButton={false}>
      <div className="px-4 pb-4 pt-10">
        <h1 className="pb-8 text-center text-xl">
          {invitationRecipients.length > 1
            ? t('enrollments.invitationModalTitle.multiple', {
                names: getInvitationRecipientsNames(),
              })
            : t('enrollments.invitationModalTitle.single', {
                name: getInvitationRecipientsNames(),
              })}
        </h1>
        <div className="flex flex-col gap-4">
          {documents?.length > 0 &&
            (documentSharingFailed ? (
              <div className="flex items-center gap-2">
                <AlertIcon className="h-10 w-10 shrink-0 text-red-500" /> {t('enrollments.documentSharingFailed')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <SuccessIcon className="h-10 w-10 text-blue-900" /> {t('enrollments.documentsSent')}
              </div>
            ))}
          <div className="flex items-center gap-2">
            <div>
              <SuccessIcon className="h-10 w-10 text-blue-900" />
            </div>
            <div>
              {t('enrollments.studentAdded', {
                name: displayName,
                status: enrollmentStatus,
              })}
            </div>
          </div>
          {nextInvoiceDate && (
            <div className="flex items-center gap-2">
              <div>
                <SuccessIcon className="h-10 w-10 text-blue-900" />
              </div>
              <div>{t('enrollments.recurringPlanSet', { date: dayjs(nextInvoiceDate).format('MM/DD') })}</div>
            </div>
          )}
          {tuitionAndFees?.tuition?.proratedDueDate &&
            tuitionAndFees?.tuition?.proratedAmount &&
            tuitionAndFees?.tuition?.proratedAmount > 0 && (
              <div className="flex items-center gap-2">
                <SuccessIcon className="h-10 w-10 text-blue-900" />{' '}
                {t('enrollments.oneTimeInvoiceSet', {
                  date: dayjs(tuitionAndFees?.tuition?.proratedDueDate).utc().format('MM/DD'),
                })}
              </div>
            )}
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-10 w-10 text-blue-900" /> {getEnrollmentText()}
          </div>
        </div>
        <div>
          <Button extraClasses="mt-10 w-full" primary onClick={onClose}>
            {t('common.done')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default NewEnrollmentInvitationConfirmationModal;
